import { createTheme } from "@mui/material/styles"

import { branding, common, palette } from "./colors"
import { fontBody, fontDisplay } from "./fonts"

// Note: global.js sets html { font-size: 100% } — 1rem === 16px, matching the
// design tokens. toRem() in utils/mixins.js divides by 16.
// MUI typography must use px units so sizes are not affected by the 10px base.

const textSecondary = "#5F5849"

export const createMuiTheme = (pageBackground = palette.background) =>
  createTheme({
  palette: {
    // Dark system — see src/theme/colors.js. Setting mode makes MUI's own
    // component defaults (dividers, disabled states, elevation) resolve for a
    // dark ground instead of inventing light ones.
    mode: "dark",
    primary: {
      main: palette.primary,
      dark: branding.active,
      light: palette.secondary,
      contrastText: palette.white,
    },
    secondary: {
      main: palette.accent,
      dark: palette.accentDark,
      light: palette.accentLight,
      contrastText: palette.background,
    },
    background: {
      default: pageBackground,
      paper: palette.secondary,
    },
    text: {
      primary: palette.dark,
      secondary: textSecondary,
    },
    success: { main: common.success },
    warning: { main: common.warning },
    error: { main: common.error },
  },
  typography: {
    fontFamily: fontBody,
    htmlFontSize: 10,
    h1: {
      fontFamily: fontDisplay,
      fontWeight: 400,
      fontSize: "48px",
      lineHeight: 1.1,
      letterSpacing: "-0.5px",
    },
    h2: {
      fontFamily: fontBody,
      fontWeight: 600,
      fontSize: "36px",
      lineHeight: 1.15,
      letterSpacing: "-0.3px",
    },
    h3: {
      fontFamily: fontBody,
      fontWeight: 500,
      fontSize: "24px",
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: fontBody,
      fontWeight: 500,
      fontSize: "16px",
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: fontBody,
      fontWeight: 600,
      fontSize: "13px",
      letterSpacing: "1px",
      textTransform: "uppercase",
    },
    body1: {
      fontFamily: fontBody,
      fontSize: "16px",
      lineHeight: 1.6,
      fontWeight: 400,
    },
    body2: {
      fontFamily: fontBody,
      fontSize: "14px",
      lineHeight: 1.5,
    },
    button: {
      fontFamily: fontBody,
      fontWeight: 500,
      textTransform: "none",
      letterSpacing: "0.3px",
      fontSize: "15px",
    },
  },
  shape: { borderRadius: 8 },
  spacing: 8,
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1440 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "10px 24px",
          fontWeight: 500,
          fontSize: "15px",
        },
        containedPrimary: {
          backgroundColor: palette.accent,
          color: palette.background,
          "&:hover": { backgroundColor: palette.accentDark },
        },
        outlinedPrimary: {
          // `primary` is a deep teal surface colour — invisible as an outline on
          // the dark ground, so outlined buttons use the accent instead.
          borderColor: palette.accent,
          color: palette.accent,
          "&:hover": {
            backgroundColor: palette.surfaceHover,
            borderColor: palette.accentLight,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { boxShadow: "none" },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: pageBackground,
          color: palette.dark,
          fontSize: "16px",
          fontFamily: fontBody,
        },
        "::selection": {
          backgroundColor: palette.accent,
          color: palette.background,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: "h1",
          h2: "h2",
          h3: "h3",
          h4: "h4",
          h5: "h5",
          body1: "p",
          body2: "p",
        },
      },
    },
  },
})

const muiTheme = createMuiTheme()

export default muiTheme
